/**
 * BYOK provider-credential API (§22).
 *
 * Wraps the Kubb-generated platform-billing credential hooks with toast meta +
 * cache invalidation, mirroring `platformBilling.ts`. The masked list never
 * carries ciphertext (SEC-8); the test endpoint returns a boolean only (SEC-5).
 */

import {
	listAiCredentialsQueryKey,
	listAiCredentialsQueryOptions,
	useDeleteAiCredential as useDeleteAiCredentialBase,
	useSetAiCredential as useSetAiCredentialBase,
	useTestAiCredential as useTestAiCredentialBase,
} from "@packages/api-client/hooks/platformBillingController";
import type { ListAiCredentials200 } from "@packages/api-client/types/platformBillingController";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

export type AiCredential = ListAiCredentials200["credentials"][number];

const aiCredentialsQueryKey = listAiCredentialsQueryKey;

/** Query options for the masked credential list. */
export const getAiCredentialsQueryOptions = (orgId: string) =>
	listAiCredentialsQueryOptions(orgId);

/** Set (create or replace) a provider key. */
export const useSetAiCredential = () => {
	const queryClient = useQueryClient();
	const { t } = useTranslation();

	return useSetAiCredentialBase({
		mutation: {
			meta: {
				toastMessages: {
					loading: t("common.saving"),
					success: t("settings.aiKeys.saveSuccess"),
				},
			},
			onSuccess: (_data, variables) => {
				queryClient.invalidateQueries({
					queryKey: aiCredentialsQueryKey(variables.organizationId),
				});
			},
		},
	});
};

/** Test a stored provider key (boolean-only result). */
export const useTestAiCredential = () => {
	const { t } = useTranslation();

	return useTestAiCredentialBase({
		mutation: {
			meta: {
				toastMessages: {
					loading: t("settings.aiKeys.testing"),
				},
			},
		},
	});
};

/** Delete a stored provider key. */
export const useDeleteAiCredential = () => {
	const queryClient = useQueryClient();
	const { t } = useTranslation();

	return useDeleteAiCredentialBase({
		mutation: {
			meta: {
				toastMessages: {
					loading: t("common.deleting"),
					success: t("settings.aiKeys.deleteSuccess"),
				},
			},
			onSuccess: (_data, variables) => {
				queryClient.invalidateQueries({
					queryKey: aiCredentialsQueryKey(variables.organizationId),
				});
			},
		},
	});
};
